import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down--hipchat-chevron-down';
import ChevronUpIcon from '@atlaskit/icon/utility/migration/chevron-up--hipchat-chevron-up';
import Lozenge from '@atlaskit/lozenge';
import { SpotlightManager, SpotlightPulse, SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlaskit/platform-feature-flags';
import Tooltip from '@atlaskit/tooltip';
import { FormStatus } from '@atlassian/proforma-common-core/form-system-models';
import { useFormIndexState } from '../../../../controllers/form-index';
import { useFormIndexActions } from '../../../../controllers/form-index/store';
import { FormCardIcon } from './form-card-icon';
import { MeatballSpotlight, SPOTLIGHT_TARGET_ID } from './meatball-spotlight';
import { messages } from './messages';
import { FormName, HeaderContainer } from './styled';
import { useHandleDropdownOpenChange } from './utils/useHandleDropdownOpenChange';
const PROFORMA_ISSUE_VIEW_FORM_MEATBALL_SEEN_STATUS_USER_PROPERTY_KEY = 'proforma-issue-view-form-meatball-seen-status';
export const FormCardHeader = ({
  form,
  formStore,
  toggleExpanded,
  renderMenu,
  onCancelEdit,
  projectType,
  hideLockedLozenge,
  isFirstForm
}) => {
  const {
    hasSeenOnboarding,
    markAsSeenOnboarding
  } = useFormIndexActions();
  const {
    selectedFormId
  } = useFormIndexState();
  const {
    isOpen,
    handleDropdownOpenChange
  } = useHandleDropdownOpenChange(onCancelEdit);
  const intl = useIntl();
  const hasFormAccess = fg('granular_permissions_on_forms_ga') ? form.hasAccess : true;
  let status;
  switch (form.status) {
    case FormStatus.Open:
      status = 'inprogress';
      break;
    case FormStatus.Submitted:
      status = 'success';
      break;
    case FormStatus.Locked:
      if (hideLockedLozenge) {
        status = 'success';
      } else {
        status = 'removed';
      }
      break;
    default:
      status = 'default';
  }

  // has the user seen the spotlight yet (from user properties)
  const [hasSeenSpotlight, setHasSeenSpotlight] = useState(true);
  const [shouldShowPulse, setShouldShowPulse] = useState(false);
  const [shouldShowSpotlight, setShouldShowSpotlight] = useState(false);

  // has the spotlight displayed on this current render
  const [hasSpotlightDisplayed, setHasSpotlightDisplayed] = useState(false);
  useEffect(() => {
    if (isFirstForm && projectType === 'service_desk') {
      hasSeenOnboarding(PROFORMA_ISSUE_VIEW_FORM_MEATBALL_SEEN_STATUS_USER_PROPERTY_KEY).then(result => {
        if (!result) {
          setHasSeenSpotlight(false);
        }
      });
    }
  }, [hasSeenOnboarding, isFirstForm, projectType]);
  useEffect(() => {
    if (!hasSeenSpotlight && !hasSpotlightDisplayed) {
      setShouldShowPulse(!hasSeenSpotlight);
    }
  }, [hasSeenSpotlight, hasSpotlightDisplayed, projectType]);
  const onDisplaySpotlight = attrs => {
    var _attrs$event;
    (_attrs$event = attrs.event) === null || _attrs$event === void 0 ? void 0 : _attrs$event.stopPropagation();
    if (shouldShowPulse) {
      setShouldShowPulse(false);
      setShouldShowSpotlight(true);
    }
  };
  const onAcknowledgeSpotlight = () => {
    setHasSeenSpotlight(true);
    setHasSpotlightDisplayed(true);
    setShouldShowSpotlight(false);
    markAsSeenOnboarding(PROFORMA_ISSUE_VIEW_FORM_MEATBALL_SEEN_STATUS_USER_PROPERTY_KEY);
  };
  const formChevronButton = /*#__PURE__*/React.createElement(React.Fragment, null, selectedFormId === form.id ? /*#__PURE__*/React.createElement(IconButton, {
    appearance: "subtle",
    icon: ChevronUpIcon,
    "aria-expanded": "true",
    label: intl.formatMessage(messages.collapseChevronLabel, {
      formName: form.name
    })
  }) : /*#__PURE__*/React.createElement(IconButton, {
    appearance: "subtle",
    icon: ChevronDownIcon,
    "aria-expanded": "false",
    label: intl.formatMessage(messages.expandChevronLabel, {
      formName: form.name
    })
  }));
  return /*#__PURE__*/React.createElement(HeaderContainer, {
    "data-test-id": "proforma-form-index-form-card-header",
    onClick: toggleExpanded
  }, /*#__PURE__*/React.createElement(FormCardIcon, {
    restrictionStatus: form.restrictionStatus
  }), /*#__PURE__*/React.createElement(FormName, {
    "data-test-id": "proforma-form-index-form-card-name"
  }, form.name), form.internal && projectType === 'service_desk' && /*#__PURE__*/React.createElement(Lozenge, {
    appearance: "default"
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.statusInternal)), /*#__PURE__*/React.createElement(React.Fragment, null, form.status === FormStatus.Locked && !hideLockedLozenge && /*#__PURE__*/React.createElement(Tooltip, {
    position: "top",
    content: /*#__PURE__*/React.createElement(FormattedMessage, messages.statusLockedTooltip)
  }, /*#__PURE__*/React.createElement(Lozenge, {
    appearance: status
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.statusLockedForEdits))), form.status === FormStatus.Open && (!form.internal || !(projectType === 'service_desk')) && /*#__PURE__*/React.createElement(Lozenge, {
    appearance: status
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.statusOpenForEdits)), (form.status === FormStatus.Submitted || form.status === FormStatus.Locked && hideLockedLozenge) && /*#__PURE__*/React.createElement(Lozenge, {
    appearance: status
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.statusSubmitted))), hasFormAccess ? isFirstForm ? /*#__PURE__*/React.createElement(SpotlightManager, null, (renderMenu || !fg('platform_jsm_issue_view_forms_meatballs_optional')) && /*#__PURE__*/React.createElement(SpotlightTarget, {
    name: SPOTLIGHT_TARGET_ID,
    key: "form-card-meatball"
  }, /*#__PURE__*/React.createElement(SpotlightPulse, {
    radius: 3,
    pulse: shouldShowPulse
  }, /*#__PURE__*/React.createElement(DropdownMenu, {
    placement: "bottom-end",
    shouldRenderToParent: true,
    testId: "proforma-form-index-form-card-menu-meatball",
    isOpen: isOpen,
    trigger: ({
      triggerRef,
      ...props
    }) => /*#__PURE__*/React.createElement(Button, _extends({}, props, {
      appearance: "subtle",
      ref: triggerRef,
      iconBefore: /*#__PURE__*/React.createElement(MoreIcon, {
        color: "currentColor",
        label: "Actions"
      })
    })),
    onOpenChange: hasSeenSpotlight ? handleDropdownOpenChange : onDisplaySpotlight
  }, renderMenu && renderMenu(form, formStore)))), shouldShowSpotlight && /*#__PURE__*/React.createElement(MeatballSpotlight, {
    onAcknowledge: onAcknowledgeSpotlight
  }), formChevronButton) : /*#__PURE__*/React.createElement(React.Fragment, null, (renderMenu || !fg('platform_jsm_issue_view_forms_meatballs_optional')) && /*#__PURE__*/React.createElement(DropdownMenu, {
    placement: "bottom-end",
    shouldRenderToParent: true,
    testId: "proforma-form-index-form-card-menu-meatball",
    isOpen: isOpen,
    trigger: ({
      triggerRef,
      ...props
    }) => /*#__PURE__*/React.createElement(Button, _extends({}, props, {
      appearance: "subtle",
      ref: triggerRef,
      iconBefore: /*#__PURE__*/React.createElement(MoreIcon, {
        color: "currentColor",
        label: "Actions"
      })
    })),
    onOpenChange: handleDropdownOpenChange
  }, renderMenu && renderMenu(form, formStore)), formChevronButton) : /*#__PURE__*/React.createElement(React.Fragment, null, formChevronButton));
};